/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import {
    Box,
    Button,
    Accordion,
    AccordionButton,
    AccordionItem,
    AccordionPanel,
    useToast,
    Stack,
    Flex,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm} from 'react-hook-form'
import PromoCodeFields from '../forms/promo-code-fields'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {MinusIcon, AddIcon} from '@chakra-ui/icons'
import {CloseIcon} from '@salesforce/retail-react-app/app/components/icons'

import {sendViewErrorEvent} from '../../utils/gtm-utils'

export const usePromoCode = () => {
    const {formatMessage} = useIntl()
    const {data: basket} = useCurrentBasket()
    const form = useForm()
    const toast = useToast()

    const applyPromoCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removePromoCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')

    const submitPromoCode = async ({code}) => {
        try {
            await applyPromoCodeMutation.mutateAsync(
                {
                    parameters: {basketId: basket?.basketId},
                    body: {
                        code
                    }
                },
                {
                    onSuccess: (data) => {
                        const {couponItems} = data
                        const invalidCoupons = couponItems.filter(
                            (item) => item.statusCode !== 'applied'
                        )

                        if (invalidCoupons.length > 0) {
                            invalidCoupons.forEach((coupon) =>
                                autoRemovePromoCode(basket?.basketId, coupon?.couponItemId)
                            )
                        }
                    }
                }
            )

            form.reset({code: ''})
        } catch (e) {
            // GTM Modification
            sendViewErrorEvent(
                'Check the code and try again, it may already be applied or the promo has expired.'
            )
            form.setError(
                'code',
                {
                    type: 'manual',
                    message: formatMessage({
                        defaultMessage:
                            'Check the code and try again, it may already be applied or the promo has expired.',
                        id: 'use_promocode.error.check_the_code'
                    })
                },
                {shouldFocus: true}
            )
        }
    }

    const autoRemovePromoCode = async (basketId, couponItemId) => {
        removePromoCodeMutation.mutate(
            {
                parameters: {basketId: basketId, couponItemId}
            },
            {
                onSettled: () => {
                    // GTM Modification
                    sendViewErrorEvent(
                        'Check the code and try again, it may already be applied or the promo has expired.'
                    )
                    form.reset({code: ''})
                    form.setError(
                        'code',
                        {
                            type: 'manual',
                            message: formatMessage({
                                defaultMessage:
                                    'Check the code and try again, it may already be applied or the promo has expired.',
                                id: 'use_promocode.error.check_the_code'
                            })
                        },
                        {shouldFocus: true}
                    )
                }
            }
        )
    }

    const removePromoCode = async (couponItemId) => {
        removePromoCodeMutation.mutate(
            {
                parameters: {basketId: basket?.basketId, couponItemId}
            },
            {
                onSuccess: () => {
                    toast({
                        title: formatMessage({
                            defaultMessage: 'Promotion removed',
                            id: 'use_promocode.info.promo_removed'
                        }),
                        status: 'success',
                        position: 'top-right',
                        isClosable: true
                    })
                },
                onError: () => {
                    toast({
                        title: formatMessage(API_ERROR_MESSAGE),
                        status: 'error',
                        position: 'top-right',
                        isClosable: true
                    })
                }
            }
        )
    }

    useEffect(() => {
        if (basket?.couponItems?.length > 0) {
            basket.couponItems
                .filter((item) => item.statusCode !== 'applied')
                .forEach((item) => {
                    autoRemovePromoCode(basket.basketId, item.couponItemId)
                })
        }
    }, [basket])

    return {form, submitPromoCode, removePromoCode}
}

export const PromoCode = ({itemProps, basket}) => {
    const [isOpen, setOpen] = useState()
    const fontSize = 'sm'
    const {removePromoCode, form, submitPromoCode} = usePromoCode()

    // Create a mapping of couponItemId to promotionName
    const promotionNamesMap = React.useMemo(() => {
        const map = {}

        // Collect order-level promotions
        basket?.orderPriceAdjustments?.forEach((adjustment) => {
            const matchingCoupon = basket?.couponItems?.find(
                (item) => item.code === adjustment.couponCode
            )
            if (matchingCoupon) {
                map[matchingCoupon.couponItemId] = adjustment.c_calloutMsg || adjustment.itemText
            }
        })

        // Collect product-level promotions
        basket?.productItems?.forEach((productItem) => {
            productItem?.priceAdjustments?.forEach((adjustment) => {
                const matchingCoupon = basket?.couponItems?.find(
                    (item) => item.code === adjustment.couponCode
                )
                if (matchingCoupon && !map[matchingCoupon.couponItemId]) {
                    map[matchingCoupon.couponItemId] = adjustment.c_calloutMsg || adjustment.itemText
                }
            })
        })

        return map
    }, [basket?.orderPriceAdjustments, basket?.productItems, basket?.couponItems])

    useEffect(() => {
        if (basket?.couponItems?.length > 0) {
            setOpen(true)
        }
    }, [form.formState.isSubmitSuccessful])

    return (
        <Accordion allowToggle index={isOpen ? 0 : -1} onChange={() => setOpen(!isOpen)} mt={6}>
            <AccordionItem {...itemProps} borderBottom="none">
                {({isExpanded}) => (
                    <>
                        <AccordionButton
                            data-testid="promo-code-accordion-button"
                            as={Button}
                            justifyContent="flex-start"
                            variant="link"
                            fontSize="sm"
                            fontWeight="700"
                            textDecoration="none"
                            textTransform="uppercase"
                            marginTop="3"
                            leftIcon={
                                isExpanded ? <MinusIcon boxSize={3} /> : <AddIcon boxSize={3} />
                            }
                            onClick={() => form.reset()}
                            _active={{color: 'black', textDecoration: 'none'}}
                            _hover={{textDecoration: 'none'}}
                        >
                            <FormattedMessage
                                defaultMessage="Promo code"
                                id="use_promo_code_fields.label.promo_code"
                            />
                        </AccordionButton>

                        <AccordionPanel px={0} mb={4}>
                            {basket?.couponItems?.length > 0 && (
                                <Stack py={3} spacing={3}>
                                    {basket.couponItems
                                        .filter((item) => item.statusCode === 'applied')
                                        .map(
                                            (item, idx) =>
                                                item?.couponItemId !==
                                                    basket?.c_sflmLoyaltyCouponID && (
                                                    <Box key={idx}>
                                                        {promotionNamesMap &&
                                                            promotionNamesMap[
                                                                item.couponItemId
                                                            ] && (
                                                                <Flex
                                                                    key={item.couponItemId}
                                                                    alignItems="center"
                                                                >
                                                                    <Text
                                                                        flex="1"
                                                                        fontSize={fontSize}
                                                                        fontWeight="700"
                                                                    >
                                                                        {
                                                                            promotionNamesMap[
                                                                                item.couponItemId
                                                                            ]
                                                                        }
                                                                    </Text>

                                                                    {!basket.orderNo && (
                                                                        <Button
                                                                            variant="link"
                                                                            size={fontSize}
                                                                            colorScheme="black"
                                                                            onClick={() =>
                                                                                removePromoCode(
                                                                                    item.couponItemId
                                                                                )
                                                                            }
                                                                        >
                                                                            <CloseIcon />
                                                                        </Button>
                                                                    )}
                                                                </Flex>
                                                            )}
                                                        <Flex alignItems="center">
                                                            <Text flex="1" fontSize={fontSize}>
                                                                {item.code}
                                                            </Text>
                                                            {!basket.orderNo &&
                                                                !(
                                                                    promotionNamesMap &&
                                                                    promotionNamesMap[
                                                                        item.couponItemId
                                                                    ]
                                                                ) && (
                                                                    <Button
                                                                        variant="link"
                                                                        size={fontSize}
                                                                        colorScheme="black"
                                                                        onClick={() =>
                                                                            removePromoCode(
                                                                                item.couponItemId
                                                                            )
                                                                        }
                                                                    >
                                                                        <CloseIcon />
                                                                    </Button>
                                                                )}
                                                        </Flex>
                                                    </Box>
                                                )
                                        )}
                                </Stack>
                            )}

                            <Box
                                data-testid="promo-code-form"
                                as="form"
                                background="white"
                                onSubmit={form.handleSubmit(submitPromoCode)}
                            >
                                <PromoCodeFields form={form} maxWidth="350px" />
                            </Box>
                        </AccordionPanel>
                    </>
                )}
            </AccordionItem>
        </Accordion>
    )
}

PromoCode.propTypes = {
    /** Props applied to inner AccordionItem. Useful for style overrides. */
    itemProps: PropTypes.object,
    basket: PropTypes.object
}

export default PromoCode
